import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const SIZE_CLASSES = {
    sm: 'sm:max-w-md',
    md: 'sm:max-w-lg',
    lg: 'sm:max-w-2xl',
    xl: 'sm:max-w-4xl',
    '2xl': 'sm:max-w-6xl',
    '90': 'sm:w-[90vw] sm:max-w-[90vw]',
    full: 'sm:max-w-full sm:mx-4'
};

const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    showClose = true,
    className = '',
}) => {
    const dialogRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKey);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'modal-title' : undefined}
        >
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-modal-fade"
                onClick={onClose}
            />

            <div
                ref={dialogRef}
                onClick={(e) => e.stopPropagation()}
                className={`
                    relative
                    z-10
                    flex flex-col
                    bg-white
                    shadow-2xl
                    w-full
                    h-[100dvh] sm:h-auto
                    sm:max-h-[90vh]
                    sm:my-4
                    rounded-none sm:rounded-2xl
                    ${sizeClass}
                    animate-modal-enter
                    ${className}
                `}
                style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                {(title || showClose) && (
                    <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-none sm:rounded-t-2xl">
                        {title && (
                            <h3 id="modal-title" className="text-base sm:text-lg font-medium text-gray-900 truncate">
                                {title}
                            </h3>
                        )}
                        {showClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Chiudi"
                                className="ml-auto -mr-1 rounded-lg p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors touch-manipulation"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4">
                    {children}
                </div>
            </div>

            <style>{`
                @keyframes modalEnter {
                    from { opacity: 0; transform: translateY(16px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @media (min-width: 640px) {
                    @keyframes modalEnter {
                        from { opacity: 0; transform: scale(0.96); }
                        to { opacity: 1; transform: scale(1); }
                    }
                }
                @keyframes modalFade {
                    from { opacity: 0; } to { opacity: 1; }
                }
                .animate-modal-enter { animation: modalEnter 0.18s ease-out forwards; }
                .animate-modal-fade { animation: modalFade 0.15s ease-out forwards; }
            `}</style>
        </div>,
        document.body
    );
};

export default Modal;
